import { useRef } from "react";
import { useModal } from "@ebay/nice-modal-react";
import { uniqueId } from "lodash";
import { t } from "@/i18n-js/instance";
import type { CommunityMember } from "@/react/types/CommunityMember";
import { ConfirmationModal } from "@circle-react-shared/ModalsV2/ConfirmationModal";
import type { OnSubmitFunction } from "@circle-react-shared/uikit/Form/Form";
import { Form } from "@circle-react-uikit/Form";
import { ModalManager } from "../../Layout/ModalManagerProvider";
import { ConnectModalContent } from "./ConnectModalContent";

interface ConnectModalComponentProps {
  isLoading?: boolean;
  onConnect: (note?: string) => void;
  recipient?: CommunityMember;
}

const i18nBase = "settings.connect_audience.connect_modal";

const ConnectModalComponent = ({
  onConnect,
  recipient,
  isLoading = false,
}: ConnectModalComponentProps) => {
  const { current: formId } = useRef(uniqueId("connect_modal_"));
  const modal = useModal();

  const handleSubmit: OnSubmitFunction<{ note?: string }> = values => {
    onConnect(values.note);
    void modal.hide();
  };

  if (!recipient) {
    return null;
  }

  return (
    <ConfirmationModal
      formId={formId}
      isOpen={modal.visible}
      onClose={modal.hide}
      title={t([i18nBase, "title"], { name: recipient?.first_name })}
      confirmVariant="circle"
      confirmText={t([i18nBase, "send_request_button"])}
      disabled={isLoading}
    >
      <Form
        id={formId}
        mode="onChange"
        onSubmit={handleSubmit}
        defaultValues={{
          note: "",
        }}
      >
        <ConnectModalContent recipient={recipient} />
      </Form>
    </ConfirmationModal>
  );
};
ConnectModalComponent.displayName = "ConnectModalComponent";

const ConnectModal = ModalManager.create(
  ({ onConnect, recipient, isLoading }: ConnectModalComponentProps) => (
    <ConnectModalComponent
      onConnect={onConnect}
      recipient={recipient}
      isLoading={isLoading}
    />
  ),
);

export const useConnectModal = () => useModal(ConnectModal);
